<template>
  <div class="form_row">
    <!-- DESCRIPTION SELECT -->
    <b-form-select
        ref="descSelect"
        v-model="selectedId"
        :options="descOptions"
        placeholder="Select description"
        style="width: 100%;"
        tabindex="0"
        @change="handleChange"
        @keydown.native="handleSelectKeyDown"
    />

    <!-- AMOUNT INPUT -->
    <b-form-input
        ref="amountInput"
        v-model.number="amount"
        min="0"
        placeholder="Enter amount..."
        step="any"
        style="width: 100%;"
        tabindex="0"
        type="number"
        @change="handleChange"
        @keydown="handleAmountKeyDown"
    />

    <!-- +ADD BUTTON -->
    <b-button
        v-if="showAddButton"
        ref="addBtn"
        :disabled="!canAdd"
        class="btn btn-info add_button"
        size="sm"
        tabindex="0"
        @click="emitAdd"
        @keydown="handleAddKeyDown"
    >
      + Add
    </b-button>
  </div>
</template>

<script>
export default {
  props: {
    value: { type: Object, required: true },
    descriptions: { type: Array, required: true },
    label: { type: String, default: '' },
    showAddButton: { type: Boolean, default: false }
  },

  data() {
    return {
      selectedId: this.value.add_ded_desc_id || null,
      amount: this.value.amount || null
    };
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.selectedId = newVal.add_ded_desc_id || null;
        this.amount = newVal.amount || null;
      }
    }
  },

  computed: {
    descOptions() {
      return this.descriptions.map(d => ({ value: d.id, text: d.value }));
    },
    selectedDescription() {
      return this.descriptions.find(d => d.id === this.selectedId) || null;
    },
    canAdd() {
      return this.selectedId !== null && this.amount !== null && !isNaN(this.amount);
    }
  },

  methods: {
    // --- helpers ---
    amountEmpty() {
      return (
          this.amount === null ||
          this.amount === '' ||
          isNaN(parseFloat(this.amount))
      );
    },

    // --- model emit ---
    handleChange() {
      this.$emit('input', {
        add_ded_desc_id: this.selectedId,
        add_ded_type_id: this.selectedDescription?.crm_attribute_master_type_id || null,
        amount: this.amount
      });
    },

    emitAdd() {
      // tell parent what to add
      this.$emit('add', {
        add_ded_desc_id: this.selectedId,
        add_ded_type_id: this.selectedDescription?.crm_attribute_master_type_id || null,
        amount: this.amount
      });
    },

    // --- keyboard nav ---
    handleAmountKeyDown(e) {
      if (['e', 'E', '+', '-'].includes(e.key)) {
        e.preventDefault();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this.$emit('reset');
        return;
      }

      if (e.key === 'ArrowLeft') {
        // leave mini-form backward; repeater will land on LAST ✕ if present
        e.preventDefault();
        this.$emit('retreat');
        return;
      }

      if (e.key === 'Tab' || e.key === 'Enter' || e.key === 'ArrowRight') {
        e.preventDefault();
        if (this.showAddButton && this.$refs.addBtn) {
          this.$refs.addBtn.focus();
        } else {
          this.$emit('advance');
        }
      }


    },

    handleSelectKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.$emit('reset');
        return;
      }

      // backward navigation
      if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
        e.preventDefault();
        this.$emit('retreat');
        return;
      }

      // forward navigation
      if (['Tab','Enter','ArrowRight'].includes(e.key)) {
        e.preventDefault();

        // Let Vue update v-model first before branching logic
        this.$nextTick(() => {
          const hasValue = !!this.selectedId;

          if (!hasValue) {
            // nothing chosen → bubble to parent repeater
            this.$emit('advance');
          } else {
            // selected → go into amount input
            this.focusAmount();
          }
        });
      }
    },

    handleAddKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.$emit('reset');
        return;
      }

      if (e.key === 'ArrowLeft') {
        // retreat out of mini-form; repeater will handle LAST ✕
        e.preventDefault();
        this.$emit('retreat');
        return;
      }
      if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
        e.preventDefault();
        if (!this.$refs.addBtn?.disabled) this.emitAdd();
        return;
      }
      if (e.key === 'Tab' || e.key === 'ArrowRight') {
        e.preventDefault();
        this.$emit('advance');
      }
    },

    // --- focus helpers ---
    focusAmount() {
      this.$nextTick(() => {
        const el = this.$refs.amountInput;
        el?.focus?.();
        try {
          el?.select?.();
        } catch (_) {}
      });
    },
    focusDesc() {
      this.$nextTick(() => {
        const el =
            this.$refs.descSelect && (this.$refs.descSelect.$el || this.$refs.descSelect);
        const native = el?.querySelector ? el.querySelector('select') : null;
        (native || el)?.focus?.();
      });
    },

    // external API
    focusDescInput() { this.focusDesc(); },
    focusAmountInput() { this.focusAmount(); }
  }
};
</script>

<style lang="scss" scoped>
.form_row {
  max-width: 350px;
  display: grid;
  grid-template-columns: 180px 100px 50px;
  height: 33px;
  grid-gap: 10px;
}

.add_button { height:33px; width:100%; background-color:#d6006e; }
.del_button { height:33px; width:100%; }

.btn:active { color:white; background-color:#d6006e; }
.btn-outline-info:active, .btn-info:hover { color:white !important; background-color:#e6007e !important; }
.btn-info { color:white; background-color:#e6007e; }
.btn-info:disabled { background-color:#ed969e; }
</style>
